import { push, replace } from 'react-router-redux';
import unescaper from 'anchor-server-common/utilities/unescaper';
import {
  creatorReducer,
  getMetadataFromStationAndStationAudioId,
  getActiveStationAudioId,
  receivePodcastMetadata,
  receiveStation,
  receiveActiveStationAudioId,
  pause,
  playOrPause,
} from './station';
import {
  APP_INIT,
  DEFAULT_PODCAST_IMAGE,
  getBaseUrl,
  getEpisodeImage,
  nextFrame,
} from '../helpers/serverRenderingUtils';
import {
  receivePageMetadata,
  updateGoogleBreadcrumbsStructuredData,
} from './pageMetadata';
import AnchorAPIError from './modules/AnchorAPI/AnchorAPIError';

export const RECEIVE_V3_CREATOR = '@@episodePreview/RECEIVE_V3_CREATOR';
export const RECEIVE_EPISODE = '@@episodePreview/RECEIVE_EPISODE';
export const RECEIVE_V3_EPISODE = '@@episodePreview/RECEIVE_V3_EPISODE';
export const RECEIVE_V3_EPISODE_LIST =
  '@@episodePreview/RECEIVE_V3_EPISODE_LIST';

const DEFAULT_IMAGE = DEFAULT_PODCAST_IMAGE;

const emptyArray = [];
const emptyObject = {};

const initialState = {
  creator: null,
  duration: 0,
  episodes: emptyArray,
  shareLinkPath: null,
  shareLinkEmbedPath: null,
  title: null,
  isV3Episode: false,
};

function episodeReducer(state = {}, action) {
  switch (action.type) {
    case APP_INIT:
      return {
        ...state,
        created: new Date(state.created),
        publishOn: new Date(state.publishOn),
      };
    case RECEIVE_V3_EPISODE_LIST: {
      return {
        ...state,
        created: new Date(state.createdUnixTimestamp * 1000),
        publishOn: new Date(state.publishOnUnixTimestamp * 1000),
      };
    }
    default:
      return state;
  }
}

// reducer
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case APP_INIT: // format date from JSON
      return {
        ...state,
        created: new Date(state.created),
        publishOn: new Date(state.publishOn),
        episodes: state.episodes.map(e => episodeReducer(e, action)),
      };
    case RECEIVE_EPISODE:
      return {
        ...state,
        creator: creatorReducer(action.creator, action),
        isV3Episode: false,
        title: unescaper(action.title),
      };
    case RECEIVE_V3_CREATOR: {
      return {
        ...state,
        creator: creatorReducer(action.payload, action),
      };
    }
    case RECEIVE_V3_EPISODE: {
      return {
        ...state,
        created: new Date(action.payload.createdUnixTimestamp * 1000),
        publishOn: new Date(action.payload.publishOnUnixTimestamp * 1000),
        duration: action.payload.duration,
        isV3Episode: true,
        shareLinkPath: action.payload.shareLinkPath,
        shareLinkEmbedPath: action.payload.shareLinkEmbedPath,
        title: unescaper(action.payload.title),
      };
    }
    case RECEIVE_V3_EPISODE_LIST: {
      return {
        ...state,
        episodes: action.payload.episodes.map(e => episodeReducer(e, action)),
      };
    }
    default:
      return state;
  }
}

// action creators
function receiveEpisode({ title }, creator) {
  return {
    type: RECEIVE_EPISODE,
    title,
    creator,
  };
}

function receiveV3Creator(creator) {
  return {
    type: RECEIVE_V3_CREATOR,
    payload: creator,
  };
}

function receiveV3Episode(episode) {
  return {
    type: RECEIVE_V3_EPISODE,
    payload: episode,
  };
}

function receiveV3EpisodeList(episodes) {
  return {
    type: RECEIVE_V3_EPISODE_LIST,
    payload: {
      episodes,
    },
  };
}

// thunks

export function fetchEpisode(
  baseUrl = '',
  episodeId,
  stationAudioId,
  autoStart
) {
  return (dispatch, getState) =>
    fetch(`${baseUrl}/api/episodes/${episodeId}`, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          dispatch(push('/embed/404'));
          return;
        }
        const {
          episode,
          audios,
          user,
          generatedImage,
          podcastUrlDictionary,
        } = responseJson;
        // old preview
        dispatch(receiveEpisode(episode, user));
        const {
          routing: { location },
        } = getState();
        // hack to get vanity slug from route, because it doesn't actually matter
        episode.vanitySlug = getVanitySlugFromLocation(location);
        const { pathname, search } = location;
        // new episode player page
        dispatch(receiveStation(episode, user, {}, null, podcastUrlDictionary));
        const { activeStationAudioId, isExpired } = getActiveStationAudioId(
          episode,
          stationAudioId
        );

        if (autoStart) {
          dispatch(pause());
          nextFrame(() => {
            dispatch(playOrPause());
          });
        }
        dispatch(receiveActiveStationAudioId(activeStationAudioId, isExpired));
        dispatch(
          receivePageMetadata(
            getMetadataFromStationAndStationAudioId(
              episode,
              user,
              stationAudioId,
              getBaseUrl() + pathname + search,
              generatedImage
            )
          )
        );
      });
}

export function fetchV3Episode(
  baseUrl = '',
  episodeId,
  stationAudioId,
  autoStart
) {
  return (dispatch, getState) =>
    fetch(`${baseUrl}/api/v3/episodes/${episodeId}`, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          const { routing } = getState();
          if (routing && routing.location.pathname.includes('/embed/')) {
            dispatch(push('/embed/404'));
          } else {
            dispatch(push('/404'));
          }
          return new AnchorAPIError(
            responseJson,
            `Server error: ${responseJson.statusText} (${responseJson.status}) - ${responseJson.url}`
          );
        }
        if (responseJson.redirect) {
          dispatch(replace(responseJson.redirect));
          return;
        }
        const {
          episode,
          episodeAudios,
          episodes,
          creator,
          userDictionary,
          podcastMetadata,
          podcastUrlDictionary,
        } = responseJson;

        dispatch(receiveV3Episode(episode));
        dispatch(receiveV3EpisodeList(episodes));
        dispatch(receiveV3Creator(creator));
        const {
          routing: { location },
        } = getState();
        const { pathname, search } = location;
        // new episode player page
        const stationFromEpisode = {
          ...episode,
          audios: episodeAudios,
          vanitySlug: creator.vanitySlug,
        };
        dispatch(
          receiveStation(
            stationFromEpisode,
            creator,
            userDictionary,
            null,
            podcastUrlDictionary
          )
        );

        dispatch(
          updateGoogleBreadcrumbsStructuredData([
            {
              name: 'Anchor',
              url: 'http://anchor.fm',
            },
            {
              name: podcastMetadata.podcastName,
              url: `http://anchor.fm/${creator.vanitySlug}`,
            },
            {
              name: episode.title,
              url: `http://anchor.fm${episode.shareLinkPath}`,
            },
          ])
        );
        dispatch(receivePodcastMetadata(podcastMetadata));
        const activeAudio = getV3ActiveAudio(episodeAudios, stationAudioId);
        if (activeAudio) {
          if (autoStart) {
            dispatch(pause());
            nextFrame(() => {
              dispatch(playOrPause());
            });
          }
          dispatch(
            receiveActiveStationAudioId(activeAudio.stationAudioId, false)
          );
        }
        dispatch(
          receivePageMetadata(
            getMetadataFromV3ProfileAndEpisode(
              stationFromEpisode,
              creator,
              podcastMetadata,
              episode,
              activeAudio,
              getEpisodeImage({ episode, podcastMetadata, creator }),
              pathname
            )
          )
        );
      });
}

export function fetchV3ProfileAndLatestEpisode(
  baseUrl = '',
  stationId,
  autoStart
) {
  return (dispatch, getState) =>
    fetch(`${baseUrl}/api/v3/profile/${stationId}`, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          const { routing } = getState();
          if (routing && routing.location.pathname.includes('/embed/')) {
            dispatch(push('/embed/404'));
          } else {
            dispatch(push('/404'));
          }
          return;
        }
        const {
          episode,
          episodeAudios,
          episodes,
          creator,
          userDictionary,
          podcastMetadata,
          podcastUrlDictionary,
          trailerUrl,
        } = responseJson;
        episode && dispatch(receiveV3Episode(episode));
        episodes && episodes.length && dispatch(receiveV3EpisodeList(episodes));
        dispatch(receiveV3Creator(creator));
        const {
          routing: { location },
        } = getState();
        const { pathname, search } = location;
        // new episode player page
        const stationFromEpisode = {
          ...episode,
          audios: episodeAudios,
          stationId,
          vanitySlug: creator.vanitySlug,
        };
        dispatch(
          receiveStation(
            stationFromEpisode,
            creator,
            userDictionary,
            null,
            podcastUrlDictionary,
            null,
            trailerUrl
          )
        );
        dispatch(
          updateGoogleBreadcrumbsStructuredData([
            {
              name: 'Anchor',
              url: 'http://anchor.fm',
            },
            {
              name: podcastMetadata.podcastName,
              url: `http://anchor.fm/${creator.vanitySlug}`,
            },
          ])
        );
        dispatch(receivePodcastMetadata(podcastMetadata));
        const activeAudio = getV3ActiveAudio(episodeAudios);
        if (activeAudio) {
          if (autoStart) {
            dispatch(pause());
            nextFrame(() => {
              dispatch(playOrPause());
            });
          }
          dispatch(
            receiveActiveStationAudioId(activeAudio.stationAudioId, false)
          );
        }
        dispatch(
          receivePageMetadata(
            getMetadataFromV3ProfileAndEpisode(
              stationFromEpisode,
              creator,
              podcastMetadata,
              null, // just base on creator
              null, // never deep-link an audio from profile level
              getEpisodeImage({ podcastMetadata, creator }),
              pathname
            )
          )
        );
        // hack - but we need to make the location no longer be a 404
        // TODO: better abstraction? This mutates state and is leaky
        if (location.state) {
          location.state.status = 200;
        }
      });
}

export function getShareUrl(episodeId, vanitySlug, activeAudio) {
  let shareSlug = `/rr/${episodeId}`;
  if (vanitySlug) {
    shareSlug = `/${vanitySlug}/episodes/${episodeId}`;
  }
  return `${getBaseUrl()}${shareSlug}${
    activeAudio && activeAudio.stationAudioId
      ? `?at=${activeAudio.stationAudioId}`
      : ''
  }`;
}

// note - expects vanitySlug set by this point
export function getShareEmbedHtml(episodeId, vanitySlug, activeAudio) {
  return `<iframe src="${getBaseUrl()}/${vanitySlug}/episodes/${episodeId}/embed${
    activeAudio && activeAudio.stationAudioId
      ? `?at=${activeAudio.stationAudioId}`
      : ''
  }" height="270px" width="400px" frameborder="0" scrolling="no"></iframe>`;
}

function getVanitySlugFromLocation(location) {
  return location.pathname.split('/')[1];
}

function getV3ActiveAudio(audios = [], stationAudioId) {
  if (!audios.length) {
    return null;
  }
  const matchingAudio = audios.find(
    audio => audio.stationAudioId.indexOf(stationAudioId) === 0
  );
  return matchingAudio || audios[0];
}

function getMetadataFromV3ProfileAndEpisode(
  station,
  creator,
  podcastMetadata,
  episode,
  activeAudio,
  generatedImage = null,
  pathname = ''
) {
  const name = unescaper(podcastMetadata.podcastName || creator.name);
  let title = name;
  let description;
  let oEmbedUrl;
  let oEmbedTitle;
  let stream;
  let twitterCard;
  let facebookType;
  let image;
  let twitterImage;
  let twitterParams;
  let rssFeedTitle;
  let rssFeedUrl;
  const isProfilePage = true;
  const isVoiceMessagePage = pathname.endsWith('/message');
  description = `Listen to Anchor audio from ${name}`;
  if (creator.bio) {
    description = unescaper(creator.bio);
  }
  if (podcastMetadata.podcastDescription) {
    description = unescaper(podcastMetadata.podcastDescription);
  }
  if (podcastMetadata.podcastName) {
    // assume a valid feed
    rssFeedTitle = podcastMetadata.podcastName;
    rssFeedUrl = `${getBaseUrl()}/s/${station.stationId}/podcast/rss`;
  }
  oEmbedUrl = `${getBaseUrl()}/api/v3/profile/${
    station.stationId
  }/oembed?title=${encodeURIComponent(title)}`;
  oEmbedTitle = title;
  // set to playback image background
  image =
    generatedImage ||
    'https://d12xoj7p9moygp.cloudfront.net/social/web-large-playback.png';
  twitterImage = image;
  if (episode) {
    description = title;
    if (episode.title) {
      title = `${unescaper(episode.title)} by ${name}`;
      oEmbedTitle = title;
    }
    if (episode.descriptionPreview) {
      description = unescaper(episode.descriptionPreview);
    }
    oEmbedUrl = `${getBaseUrl()}/api/v3/episodes/${
      episode.episodeId
    }/oembed?title=${encodeURIComponent(title)}`;
  }
  if (isVoiceMessagePage) {
    description =
      'Send in a voice message and you could appear on a future episode of this podcast!';
  }
  return {
    description,
    facebookType,
    image,
    oEmbedUrl,
    oEmbedTitle,
    rssFeedTitle,
    rssFeedUrl,
    stream,
    title,
    twitterCard,
    twitterImage,
    twitterParams,
    isProfilePage,
    isVoiceMessagePage,
  };
}
